import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  GlobalStyles,
  IconButton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ArrowBack, MoreVert } from '@mui/icons-material';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppBar } from 'components/AppBar/AppBar';
import { NoResources } from 'components/NoResources';
import { KyronMenu } from 'components/KyronMenu';
import { useFeatures } from 'components/FeaturesContext';
import { SectionListEditorSkeleton } from 'components/Studio/Editor/SectionListEditorSkeleton';
import {
  useLessonQuery,
  useLessonSectionsQuery,
  useLessonDetailsQuery,
  useGetLatestDraftLessonQuery,
} from 'controllers/react-query';
import { BackgroundTask } from 'controllers/types';
import {
  getGenerateLessonBackgroundJob,
  getPublishBackgroundJob,
  getGenerateVideoBackgroundJob,
  useProgressBarChannel,
  generateSafeBGTaskErrorText,
} from '../../BackgroundTasks/useProgressBarChannel';
import { ErrorIndicator } from '../../ErrorIndicator';
import { LessonNameField } from '../components/LessonNameField';
import { PublishStudioLessonButton } from '../components/PublishStudioLessonButton';
import { useModal } from '../../utils/ModalContext';
import { Player } from '../../Player/Player';
import { queryClient } from '../../../controllers/react-query/config/queryClient';
import { LessonStatusTags } from './LessonStatusTags';
import { SectionListEditor } from './SectionListEditor';

// export for testing
export function useLessonAndSectionsQuery(lessonId: number) {
  const { error: lessonError, isFetching: isLoadingLesson, data: lesson } = useLessonQuery(lessonId);
  const {
    error: sectionError,
    isFetching: isLoadingSections,
    data: lessonSectionsData,
  } = useLessonSectionsQuery(lessonId);
  const {
    error: segmentsError,
    isFetching: isLoadingSegments,
    data: lessonDetailsData,
  } = useLessonDetailsQuery(lessonId);
  const { lesson_sections: lessonSections = [] } = lessonSectionsData || {};
  const { lesson_segment_background_tasks: lessonSegmentBackgroundTasks } = lessonDetailsData || {};
  const isLoading = isLoadingLesson || isLoadingSections || isLoadingSegments;
  const errors: Error[] = [lessonError, sectionError, segmentsError].filter((e): e is Error => !!e);

  return { isLoading, errors, lesson, lessonSections, lessonSegmentBackgroundTasks };
}

export function useNavigateToNewDraft(publishStatus: string | null | undefined, lessonId: number) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(publishStatus);

  const {
    error: draftError,
    isFetching: draftFetching,
    data: draftLesson,
    refetch,
  } = useGetLatestDraftLessonQuery(lessonId);

  if (publishStatus !== status && publishStatus === 'done') refetch();
  if (publishStatus !== status) setStatus(publishStatus);

  useEffect(() => {
    if (!draftFetching && !draftError && draftLesson && draftLesson.id !== lessonId) {
      navigate(`/studio/${draftLesson.id}`, { replace: true });
    }
  }, [draftFetching, draftLesson]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function Editor() {
  const isMobileView = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  const { openModal } = useModal();
  const { lessonId } = useParams<{ lessonId: string }>();
  const location = useLocation();
  const {
    lesson,
    lessonSections,
    lessonSegmentBackgroundTasks,
    errors: sectionErrors,
  } = useLessonAndSectionsQuery(Number(lessonId));

  // handle all background job statuses
  // 1- lesson creation
  // 2- lesson publishing
  // 3- lesson segment video generation
  function hasRunningBackgroundTasks(
    backgroundTask: BackgroundTask | undefined,
    progressStatus: string | undefined | null,
  ) {
    const isBackgroundTaskResultedInError = backgroundTask?.status === 'error';
    const isBackgroundTaskFinished = backgroundTask?.status === 'done' && progressStatus === undefined;
    const noBackgroundTaskInProgress = backgroundTask === undefined && progressStatus === undefined;

    return !(
      noBackgroundTaskInProgress ||
      isBackgroundTaskFinished ||
      isBackgroundTaskResultedInError ||
      progressStatus === 'done'
    );
  }

  function invalidateLessonSegments(bt: BackgroundTask) {
    queryClient.invalidateQueries({ queryKey: [`/lesson_segments/${bt.task_entity?.id}`] });
  }

  function invalidateLessonSections(bt: BackgroundTask) {
    queryClient.invalidateQueries({ queryKey: [`/lessons/${bt.task_entity_id}/lesson_sections`] });
  }

  const creationBackgroundJob = getGenerateLessonBackgroundJob(lesson);

  const lessonCreationTask = useProgressBarChannel(creationBackgroundJob, invalidateLessonSections);
  const creationBackgroundTaskRunning = hasRunningBackgroundTasks(creationBackgroundJob, lessonCreationTask.status);

  const publishBackgroundJob = getPublishBackgroundJob(lesson);
  const publishTask = useProgressBarChannel(publishBackgroundJob, invalidateLessonSections);

  const publishingBackgroundTaskRunning = hasRunningBackgroundTasks(publishBackgroundJob, publishTask.status);

  const generateVideoBackgroundJob = getGenerateVideoBackgroundJob(lesson?.id || 0, lessonSegmentBackgroundTasks);
  const videoGenerationTask = useProgressBarChannel(generateVideoBackgroundJob, invalidateLessonSegments);
  const videoBackgroundTaskRunning = hasRunningBackgroundTasks(generateVideoBackgroundJob, videoGenerationTask.status);
  const { show_lesson_settings_button: showLessonSettingsButton } = useFeatures();

  const isCreatingLesson = lessonSections.length === 0 && creationBackgroundTaskRunning;
  useNavigateToNewDraft(publishTask.status, Number(lessonId));

  if (!lessonId) return <NoResources message='No lesson selected' />;

  const publishError =
    publishTask.status === 'error' || publishBackgroundJob?.status === 'error'
      ? new Error(generateSafeBGTaskErrorText('publishing', publishTask))
      : null;

  const createError =
    lessonCreationTask.status === 'error'
      ? new Error(generateSafeBGTaskErrorText('lesson creation', lessonCreationTask))
      : null;

  const videoGenerateError =
    videoGenerationTask.status === 'error'
      ? new Error(generateSafeBGTaskErrorText('video generation', videoGenerationTask))
      : null;

  const menuActions = (
    <>
      {showLessonSettingsButton && (
        <Button
          component={Link}
          to='settings'
          state={{ previousLocation: location }}
          variant='outlined'
          disabled={creationBackgroundTaskRunning || publishingBackgroundTaskRunning || videoBackgroundTaskRunning}
        >
          Lesson settings
        </Button>
      )}
      <Button
        variant='outlined'
        disabled={creationBackgroundTaskRunning}
        onClick={() => {
          if (!lessonId) return;
          openModal({
            fullWidth: true,
            themeMode: 'dark',
            id: 'draft-lesson-preview',
            title: 'Draft Lesson Preview',
            content: (
              //  Just a note, same content can be reachable by going to the path: /video_player/${lessonId}?restart=true&force_separate=true
              <Box sx={{ position: 'relative', width: '100%', height: 'calc(80vh)', mb: 2 }}>
                <Player lessonId={lessonId} restart />
              </Box>
            ),
          });
        }}
      >
        Preview Lesson
      </Button>
      <PublishStudioLessonButton
        lessonId={lesson?.id}
        disabled={creationBackgroundTaskRunning || publishingBackgroundTaskRunning || videoBackgroundTaskRunning}
      />
      <ErrorIndicator error={createError || videoGenerateError} />
      <ErrorIndicator
        error={publishError}
        description='Sorry, your request to publish this lesson has failed. There was a problem during our process. Please retry.'
      />
    </>
  );

  const lessonNameField = lesson ? <LessonNameField lesson={lesson} /> : 'Loading...';

  return (
    <>
      <GlobalStyles styles={theme => ({ body: { backgroundColor: theme.palette.background.paper } })} />

      <AppBar
        showLogo={false}
        title={isMobileView && lessonNameField}
        navButton={
          <IconButton to='/studio/library' component={Link}>
            <ArrowBack />
          </IconButton>
        }
        actions={
          isMobileView ? (
            <KyronMenu menuIcon={<MoreVert />}>
              <Stack m={1} gap={1}>
                {menuActions}
              </Stack>
            </KyronMenu>
          ) : (
            menuActions
          )
        }
        mainToolBarSx={{ bgcolor: 'background.paper' }}
      />

      <Box px={2}>
        {publishingBackgroundTaskRunning ? (
          <Alert severity='info'>
            <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
              Your lesson is being published.
              <CircularProgress aria-label='progress-bar-loading' size={20} />
            </Stack>
          </Alert>
        ) : null}

        {creationBackgroundTaskRunning ? (
          <Alert severity='info'>
            <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
              Your lesson is being created. You can edit and publish once it&apos;s ready.
              <CircularProgress aria-label='progress-bar-loading' size={20} />
            </Stack>
          </Alert>
        ) : null}

        {videoBackgroundTaskRunning ? (
          <Alert severity='info'>
            <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
              Your video is being generated.
              <CircularProgress aria-label='progress-bar-loading' size={20} />
            </Stack>
          </Alert>
        ) : null}

        {sectionErrors.length > 0 ? (
          <Stack gap={1} ml={2} mr={2}>
            {sectionErrors.map(error => (
              <Alert key={error.message} severity='error'>
                {error.message}
              </Alert>
            ))}
          </Stack>
        ) : null}
        <Stack sx={{ maxWidth: '1380px', m: '0 auto', px: 6, py: 8, gap: 1 }}>
          <LessonStatusTags lesson={lesson} />
          <Box>
            <Typography variant='displayMedium'>{lesson?.name}</Typography>
          </Box>
          <Box height={64} />
          {isCreatingLesson ? <SectionListEditorSkeleton /> : <SectionListEditor lessonSections={lessonSections} />}
        </Stack>
      </Box>
    </>
  );
}
